import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { VerifiedUserOutlined, HandshakeOutlined, MonetizationOnOutlined, LocationOnOutlined, HistoryEduOutlined, ArrowForward, CheckCircleOutline, StorefrontOutlined, LightbulbOutlined } from '@mui/icons-material';
import '../styles/SeoPages.css';
import '../styles/common.css';

const TrustedJewelleryStorePage = () => {
    useEffect(() => {
        document.title = "Trusted Jewellery Store in Jodhpur & Rajasthan | MAB Jewels";

        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute("content", "Learn why MAB Jewels, formerly Manoj Abhushan Bhandar, is a trusted jewellery store in Jodhpur and across Rajasthan.");
        }
    }, []);

    return (
        <div className="page-container seo-page">
            <div className="seo-content-wrapper">
                <h1>Why MAB Jewels Is a Trusted Jewellery Store in Jodhpur & Rajasthan</h1>

                <p className="seo-intro">
                    Trust is the foundation of jewellery buying, especially in Rajasthan where gold jewellery holds cultural, emotional, and financial value. <strong>MAB Jewels</strong>, built on the legacy of <strong>Manoj Abhushan Bhandar</strong>, has earned the trust of families across Jodhpur and Rajasthan through honesty, transparency, and certified purity.
                </p>

                <p>
                    For decades, customers have relied on MAB Jewels for gold and silver jewellery that reflects tradition, craftsmanship, and long-term value.
                </p>

                <h2>Legacy of Manoj Abhushan Bhandar</h2>
                <div style={{ background: '#fff9e6', padding: '25px', borderRadius: '12px', border: '1px solid #d4af37', marginBottom: '30px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                        <HistoryEduOutlined sx={{ fontSize: 40, color: '#d4af37', marginTop: '5px' }} />
                        <div>
                            <p style={{ marginTop: 0 }}><strong>Manoj Abhushan Bhandar</strong> has been a well-known jewellery name in Rajasthan, trusted for fair pricing and genuine craftsmanship.</p>
                            <p style={{ marginBottom: 0 }}>MAB Jewels carries this legacy forward with a modern approach while maintaining the same core values of trust and integrity.</p>
                        </div>
                    </div>
                </div>
                <p>This continuity ensures that customers searching for Manoj Abhushan Bhandar or MAB Jewels are reaching the same trusted jewellery business.</p>

                <h2>Certified Gold & Silver Jewellery</h2>
                <p>
                    One of the key reasons MAB Jewels is considered a trusted jewellery store in Jodhpur and Rajasthan is its strict commitment to purity.
                </p>
                <p>At MAB Jewels:</p>
                <ul className="seo-check-list">
                    <li><VerifiedUserOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> All gold jewellery is BIS Hallmark certified</li>
                    <li><VerifiedUserOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Clear purity markings are provided</li>
                    <li><VerifiedUserOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Proper invoices mention weight, purity, and pricing</li>
                    <li><VerifiedUserOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Customers receive transparent price breakup</li>
                </ul>
                <div className="seo-highlight-box">
                    <p><CheckCircleOutline sx={{ color: '#d4af37', verticalAlign: 'middle', mr: 1 }} /> <strong>Promise:</strong> This ensures confidence and peace of mind for every purchase.</p>
                </div>

                <h2>Transparent Pricing & Honest Practices</h2>
                <p>
                    Gold prices change daily, and transparency is essential for customer trust. MAB Jewels follows:
                </p>
                <div className="seo-steps-list">
                    <h3><MonetizationOnOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Fair Pricing Model</h3>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}><ArrowForward sx={{ fontSize: 16, color: '#d4af37', mr: 1 }} /> Pricing aligned with live market gold rates</li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}><ArrowForward sx={{ fontSize: 16, color: '#d4af37', mr: 1 }} /> Clear explanation of making charges</li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}><ArrowForward sx={{ fontSize: 16, color: '#d4af37', mr: 1 }} /> No hidden costs</li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}><ArrowForward sx={{ fontSize: 16, color: '#d4af37', mr: 1 }} /> Honest guidance during purchase decisions</li>
                    </ul>
                </div>
                <p>These practices have helped MAB Jewels earn long-term trust as a reliable jewellery shop in Jodhpur.</p>

                <h2>Trusted Jewellery Store in Jodhpur</h2>
                <p>
                    Based in Rajasthan, MAB Jewels serves customers from Jodhpur and nearby regions, offering jewellery for weddings, festivals, and everyday wear.
                </p>
                <p>Customers prefer MAB Jewels for:</p>
                <ul className="seo-check-list">
                    <li><StorefrontOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Wedding jewellery guidance</li>
                    <li><StorefrontOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Custom jewellery designs</li>
                    <li><StorefrontOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Traditional and modern collections</li>
                    <li><StorefrontOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Reliable after-sales support</li>
                </ul>
                <p>This local presence strengthens its reputation as a trusted jewellery store in Jodhpur.</p>

                <h2>Serving Customers Across Rajasthan</h2>
                <p>
                    Beyond Jodhpur, MAB Jewels is trusted by customers across Rajasthan for certified gold jewellery and fair dealings. The brand's reputation has grown through word-of-mouth, repeat customers, and consistent quality. This regional trust positions MAB Jewels as a reliable jewellery store in Rajasthan.
                </p>

                <h2>Why Customers Choose MAB Jewels</h2>
                <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #eee' }}>
                    <ul className="seo-check-list" style={{ marginTop: 0 }}>
                        <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Legacy of Manoj Abhushan Bhandar</li>
                        <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> BIS Hallmark certified jewellery</li>
                        <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Transparent pricing and invoices</li>
                        <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Honest guidance and customer-first approach</li>
                        <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Strong local presence in Rajasthan</li>
                    </ul>
                </div>

                <h2>FAQs – MAB Jewels & Jewellery Trust</h2>
                <div className="seo-faq-section">
                    <div className="seo-faq-item">
                        <h4>Q1. Is MAB Jewels the same as Manoj Abhushan Bhandar?</h4>
                        <p>Yes. MAB Jewels carries forward the trusted legacy of Manoj Abhushan Bhandar, offering the same values with a modern approach.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Q2. Why is MAB Jewels trusted in Jodhpur?</h4>
                        <p>Because of certified gold jewellery, transparent pricing, and long-standing customer trust.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Q3. Does MAB Jewels provide BIS hallmark gold?</h4>
                        <p>Yes. All gold jewellery at MAB Jewels is BIS Hallmark certified.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Q4. Where is MAB Jewels located?</h4>
                        <p>MAB Jewels is based in Rajasthan and serves customers in Jodhpur and across the state.</p>
                    </div>
                </div>

                <div className="internal-links-section" style={{ marginTop: '40px', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
                    <h3>Explore More</h3>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/gold-jewellery-jodhpur">Gold Jewellery in Jodhpur</Link></li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/wedding-jewellery-rajasthan">Wedding Jewellery in Rajasthan</Link></li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/gold-rate-rajasthan">Today Gold Rate in Rajasthan</Link></li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/how-to-check-gold-purity-rajasthan">How to Check Gold Purity</Link></li>
                    </ul>
                    <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #ddd', textAlign: 'center' }}>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: '#555' }}>
                            Explore more gold insights at <Link to="/gold-guide" style={{ color: '#d4af37', fontWeight: 'bold', textDecoration: 'none' }}>Gold Guide by MAB Jewels</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrustedJewelleryStorePage;
